// CSS import passthrough helpers for top-level import handling.
// Example: `@import "./base.css" screen;` keeps media tails while href is normalized.
// RegExp matches escaped quotes and outputs `https://cdn/app.css` safely. Example: `app\".css`.
import { locateImportRequestRange } from './handler-css-range'

// Build normalized css-pass import statements for vendor-fast `.luis` sources.
export function buildCssPassthroughImport(statement: string, href: string): string {
  const requestRange = locateImportRequestRange(statement)
  // Check whether the request token range was resolved safely.
  if (!requestRange) {
    // Return a safe absolute import when token range parsing fails.
    return `@import "${escapeImportHref(href)}";`
  } else {
    // Continue request token replacement on original statement.
  }

  const before = statement.slice(0, requestRange.start)
  const after = statement.slice(requestRange.end)
  // Return the statement with normalized href and preserved import tail.
  return `${before}"${escapeImportHref(href)}"${after}`
}

// Resolve CSS import href against the current source id when it is a URL.
export function resolveCssImportHref(request: string, sourceId: string): string | null {
  const trimmedRequest = request.trim()
  // Check whether the CSS import request is empty.
  if (!trimmedRequest) {
    // Return null for empty CSS import requests.
    return null
  } else if (isAbsoluteImportRequest(trimmedRequest)) {
    // Return absolute CSS import requests unchanged.
    return trimmedRequest
  } else if (!isAbsoluteImportRequest(sourceId)) {
    // Return null when the importer is not a URL context.
    return null
  } else {
    // Continue URL-based import resolution.
  }

  try {
    // Return the absolute CSS href resolved from the importer source id.
    return new URL(trimmedRequest, sourceId).href
  } catch {
    // Return null when URL resolution fails.
    return null
  }
}

// Escape absolute href for double-quoted import token.
function escapeImportHref(href: string): string {
  // Return href with CSS-safe slash and quote escaping.
  return href.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

// Detect absolute request formats that do not require base resolution.
function isAbsoluteImportRequest(request: string): boolean {
  const normalized = request.trim().toLowerCase()
  // Return true for absolute URL-like import requests.
  return normalized.startsWith('http://')
    || normalized.startsWith('https://')
    || normalized.startsWith('//')
    || normalized.startsWith('data:')
}
